import React, { memo } from 'react';
import classNames from 'classnames';
import { isDeepChanged } from '../../../utils/ChangeSpy';
import FormGroup from '../../helpers/FormGroup';
import BaseTextarea from '../../helpers/Textarea';
import BaseInput from '../../helpers/Input';

import PrettyError from '../../decorators/PrettyError';
import DisplayError from '../../decorators/DisplayError';
import FiscalYearTable from './FiscalYearTable';
import { EntityType } from './EntityType';
import { DataQuality } from './DataQuality';
import { FinancialYearEnd } from './FinancialYearEnd';

import style from './style.module.css';

const Textarea = PrettyError(DisplayError(BaseTextarea));
const Input = PrettyError(DisplayError(BaseInput));

/**
 * Stateless component for contact's activities.
 * Display table where rows are activities.
 *
 * @link http://screencloud.net/v/jS6q on page (right top part)
 * @link http://screencloud.net/v/7yc2 component itself
 * @param props {Object}.
 */
const CompanyFinancialsComponent = ({ canEditData, company, onChange, onYearChange, ...rest }) => {
  const headerClass = classNames('panel-title', {
    'pl5 changed': isDeepChanged(company.get('targetFiscalYears')),
  });

  return (
    <div className="panel panel-default">
      <div className="panel-heading" data-parent="#accordion" data-target="#acc-slide-7" data-toggle="collapse">
        <h4 className={headerClass}>Financials</h4>
      </div>
      <div className={classNames(style.collapse, 'panel-collapse collapse')} id="acc-slide-7">
        <div className="panel-body">
          <div className="highlight">
            <form className="row flex-grid flex-grid__start">
              <div className="col-xl-2 col-lg-3 col-md-4">
                <FinancialYearEnd disabled={!canEditData} name="fyeMonth" onChange={onChange} root={company} deep />
              </div>
              <div className="col-xl-2 col-lg-3 col-md-4">
                <DataQuality disabled={!canEditData} name="dataQuality" onChange={onChange} root={company} deep />
              </div>
              <div className="col-xl-2 col-lg-3 col-md-4">
                <EntityType disabled={!canEditData} name="corporationType" onChange={onChange} root={company} deep />
              </div>
            </form>

            <div className="row">
              <div className="container-fluid">
                <FiscalYearTable
                  {...rest}
                  disabled={!canEditData}
                  onChange={onYearChange}
                  targetFiscalYears={company.get('targetFiscalYears')}
                />
              </div>
            </div>

            <form className="mt25">
              <b>Balance Sheet Data</b>
              <div className="row">
                <div className="col-xs-3">
                  <FormGroup>
                    <Input
                      disabled={!canEditData}
                      label="Date"
                      name="balanceSheetDate"
                      onChange={onChange}
                      placeholder="Date"
                      root={company}
                      type="date"
                      deep
                      showLabel
                    />
                  </FormGroup>
                </div>
                <div className="col-xs-3">
                  <FormGroup>
                    <Input
                      disabled={!canEditData}
                      label="Current Cash"
                      name="balanceSheetCurrentCash"
                      placeholder="Current Cash"
                      root={company}
                      deep
                      showLabel
                    />
                  </FormGroup>
                </div>
                <div className="col-xs-3">
                  <FormGroup>
                    <Input
                      disabled={!canEditData}
                      label="Current Debt"
                      name="balanceSheetCurrentDebt"
                      placeholder="Current Debt"
                      root={company}
                      deep
                      showLabel
                    />
                  </FormGroup>
                </div>
                <div className="col-xs-3">
                  <FormGroup>
                    <Input
                      className={style.ebitda}
                      disabled={!canEditData}
                      label="Curr. EBITDA"
                      name="ebitda"
                      placeholder=""
                      root={company}
                      deep
                      showLabel
                    />
                  </FormGroup>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <FormGroup>
                    <Textarea
                      disabled={!canEditData}
                      label="Financial Notes"
                      name="financialNotes"
                      placeholder="Financial Notes"
                      root={company}
                      rows="5"
                      deep
                      showLabel
                    />
                  </FormGroup>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Company financials component. */
export const CompanyFinancials = memo(CompanyFinancialsComponent);
